/**
 * Authorization Rules for Frontend Route Access Control
 * This file defines which roles are allowed to access different parts of the application
 *
 * Valid roles (from backend):
 * - ADMIN, SUPERADMIN: Full system access
 * - CH: Chairman
 * - MP: Member Planning
 * - ED: Executive Director
 * - GM: General Manager
 */

import { getActivePostShortNameBase } from '../services/postSelection';
import { isPostRestrictedFromAssignedQueries } from '../services/restrictedPostsConfig';

/**
 * Normalize role to uppercase for comparison
 * @param {string} role - Role to normalize
 * @returns {string} Normalized role in uppercase
 */
export const normalizeRole = (role) => {
  if (!role) return null;
  return String(role).trim().toUpperCase();
};

/**
 * Valid roles that are allowed in the system
 */
export const VALID_ROLES = new Set([
  'ADMIN',
  'SUPERADMIN',
  'CH',
  'MP',
  'ED',
  'GM',
]);

/**
 * Check if a role is valid (exists in the system)
 * @param {string} role - Role to validate
 * @returns {boolean} True if role is valid
 */
export const isValidRole = (role) => {
  const normalized = normalizeRole(role);
  return normalized && VALID_ROLES.has(normalized);
};

/**
 * Route access permissions by role
 * Defines which roles can access which routes
 */
export const ROUTE_PERMISSIONS = {
  // Admin only routes
  'queries/all': (role) => {
    const normalized = normalizeRole(role);
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Raise query route - CH, MP, ED can raise, GM and Admin cannot
  'queries/raise': (role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    const isAdmin = normalized === 'ADMIN' || normalized === 'SUPERADMIN';
    const isGM = normalized === 'GM';
    // Block admin and GM roles from raising queries
    return !isAdmin && !isGM;
  },

  // Raise query create route - Same as raise
  'queries/raise/new': (role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    const isAdmin = normalized === 'ADMIN' || normalized === 'SUPERADMIN';
    const isGM = normalized === 'GM';
    return !isAdmin && !isGM;
  },

  // Assigned queries - any non-admin role can view, except while the active
  // post is configured as restricted (RESTRICTED_ASSIGNED_QUERIES_POSTS)
  'queries/assigned': (role, user) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    const isAdmin = normalized === 'ADMIN' || normalized === 'SUPERADMIN';
    if (isAdmin) return false;
    return !isPostRestrictedFromAssignedQueries(getActivePostShortNameBase(user));
  },

  // Escalated queries - Everyone except admin can view
  'queries/escalated': (role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    const isAdmin = normalized === 'ADMIN' || normalized === 'SUPERADMIN';
    return !isAdmin;
  },

  // Closed queries - Everyone can view
  'queries/closed': (role) => {
    const normalized = normalizeRole(role);
    return normalized !== null;
  },

  // Projects - Admin and designated roles only
  'projects': (role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    // Admin roles can access
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Admin users route
  'admin/users': (role) => {
    const normalized = normalizeRole(role);
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Admin roles route
  'admin/roles': (role) => {
    const normalized = normalizeRole(role);
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Escalation matrix route
  'admin/escalation-matrix': (role) => {
    const normalized = normalizeRole(role);
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Analytics SLA dashboard
  'analytics/sla': (role) => {
    const normalized = normalizeRole(role);
    return normalized === 'ADMIN' || normalized === 'SUPERADMIN';
  },

  // Analytics PowerBI dashboard - Admin, CH, MP, ED
  'analytics/powerbi': (role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    return (
      normalized === 'ADMIN' ||
      normalized === 'SUPERADMIN' ||
      normalized === 'CH' ||
      normalized === 'MP' ||
      normalized === 'ED'
    );
  },

  // Settings - Everyone
  'settings': (role) => {
    const normalized = normalizeRole(role);
    return normalized !== null;
  },

  // Help - Everyone
  'help': (role) => {
    const normalized = normalizeRole(role);
    return normalized !== null;
  },
};

/**
 * Check if user can access a specific route
 * @param {string} routePath - Path of the route (without /dashboard/)
 * @param {Object} user - User object with role property
 * @returns {boolean} True if user can access the route
 */
export const canAccessRoute = (routePath, user) => {
  if (!user || !routePath) return false;

  const permissionChecker = ROUTE_PERMISSIONS[routePath];
  
  // If no specific permission rule exists, deny access
  if (!permissionChecker) return false;

  try {
    return permissionChecker(user.role, user);
  } catch (error) {
    // console.error('[AuthorizationRules] Error checking route permission:', {
      // routePath,
      // role: user.role,
      // error: error.message,
    // });
    return false;
  }
};

/**
 * Check if user has a valid role
 * @param {Object} user - User object
 * @returns {boolean} True if user has a valid role
 */
export const hasValidRole = (user) => {
  if (!user || !user.role) return false;
  return isValidRole(user.role);
};

/**
 * Get user's dashboard redirect path based on role
 * @param {Object} user - User object
 * @returns {string|null} Redirect path or null if invalid role
 */
export const getDefaultDashboardPath = (user) => {
  if (!user || !user.role) return null;

  const normalized = normalizeRole(user.role);

  // Admin users go to SLA Dashboard
  if (normalized === 'ADMIN' || normalized === 'SUPERADMIN') {
    return '/dashboard/analytics/sla';
  }

  // Chairman, MP, ED and others: go to My Queries (no longer PowerBI)
  if (normalized === 'CH' || normalized === 'MP' || normalized === 'ED') {
    return '/dashboard/queries/raise';
  }

  // GM users go to Assigned Queries
  if (normalized === 'GM') {
    return '/dashboard/queries/assigned';
  }

  // Invalid role - should not reach here
  return null;
};
